import { Box, HStack, Icon, Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import type { IconType } from "react-icons";
import { FiAward, FiCheckCircle, FiClock, FiEye, FiXCircle } from "react-icons/fi";
import type { BuyerBidV2DetailDto } from "../../../../autogen/bff-api";
import { useDisplayDateWithTime } from "../../../../common/formatting/useDisplayDateWithTime";
import { SectionCard } from "../../../sourcing-events/portal/view-single/SectionCard";
import { getBidActivityEvents, type BidActivityType } from "./bidActivity";

interface Props {
  detail: BuyerBidV2DetailDto;
}

// Activity labels are rendered via `t(item.labelKey)` below; list them as literal `t("...")` here
// so i18next-parser keeps the keys in the locale files (it can't see the data-driven call).
// t("Bid delivered") t("Opened by buyer") t("Rejected") t("Selected as winner") t("No activity has been recorded")
const meta: Record<BidActivityType, { icon: IconType; labelKey: string; color: string }> = {
  submitted: { icon: FiCheckCircle, labelKey: "Bid delivered", color: "green.500" },
  opened: { icon: FiEye, labelKey: "Opened by buyer", color: "smPrimary" },
  rejected: { icon: FiXCircle, labelKey: "Rejected", color: "red.500" },
  awarded: { icon: FiAward, labelKey: "Selected as winner", color: "green.500" },
};

export const BidActivityTimeline = ({ detail }: Props) => {
  const { t } = useTranslation();
  const displayDateTime = useDisplayDateWithTime();
  const events = getBidActivityEvents(detail);

  return (
    <SectionCard icon={FiClock} title={t("Activity")}>
      {events.length === 0 ? (
        <Text fontSize="sm" color="smMuted">
          {t("No activity has been recorded")}
        </Text>
      ) : (
        <Stack spacing="4">
          {events.map((event) => {
            const item = meta[event.type];
            return (
              <HStack key={`${event.type}-${event.at}`} spacing="3" align="flex-start">
                <Box rounded="full" bg="smBackgroundSecondary" p="1.5">
                  <Icon as={item.icon} boxSize="4" color={item.color} />
                </Box>
                <Stack spacing="0">
                  <Text fontSize="sm" fontWeight="semibold" color="smText">
                    {t(item.labelKey)}
                  </Text>
                  <Text fontSize="sm" color="smMuted">
                    {displayDateTime(event.at)}
                  </Text>
                </Stack>
              </HStack>
            );
          })}
        </Stack>
      )}
    </SectionCard>
  );
};
